import React, { createContext, useContext, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import config from '../config/environment';

const QRSessionContext = createContext();

export const useQRSession = () => {
  const context = useContext(QRSessionContext);
  if (!context) {
    throw new Error('useQRSession must be used within a QRSessionProvider');
  }
  return context;
};

export const QRSessionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [currentSession, setCurrentSession] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentSequence, setCurrentSequence] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Create new session
  const createSession = async (sessionData) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Creating session without authentication (testing mode)');

      const response = await fetch(`${config.API_BASE_URL}/qr-sessions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sessionData)
      });

      const data = await response.json();
      console.log('Create session response:', data);

      if (!data.success) {
        throw new Error(data.message);
      }

      setCurrentSession(data.session);
      return data.session;
    } catch (err) {
      console.error('Create session error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Start QR generation
  const startQRGeneration = async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Starting QR generation for session:', sessionId);

      const response = await fetch(`${config.API_BASE_URL}/qr-sessions/${sessionId}/start-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('QR generation response:', data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to start QR generation');
      }

      setQrCode(data.qr_code);
      setIsActive(true);
      setCurrentSequence(data.qr_payload?.qr_sequence || 1);
      setTimeRemaining(180); // 3 minutes

      // Start the QR refresh interval
      startQRRefresh(sessionId);

      console.log('QR generation started successfully');
      return data;
    } catch (err) {
      console.error('Error starting QR generation:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Start QR refresh interval
  const startQRRefresh = (sessionId) => {
    console.log('Starting QR refresh interval for session:', sessionId);
    
    // Clear existing intervals
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Update QR every 10 seconds
    intervalRef.current = setInterval(async () => {
      try {
        console.log('Refreshing QR code...');
        const response = await fetch(`${config.API_BASE_URL}/qr-sessions/${sessionId}/current-qr`, {
          method: 'GET'
        });

        const data = await response.json();

        if (data.success) {
          setQrCode(data.qr_code);
          setCurrentSequence(data.current_sequence);
          setTimeRemaining(Math.floor(data.time_remaining / 1000));
          console.log('QR refreshed - sequence:', data.current_sequence, 'time remaining:', Math.floor(data.time_remaining / 1000));
        } else {
          console.log('QR session expired:', data.message);
          // Session expired
          stopQRGeneration();
        }
      } catch (err) {
        console.error('Error refreshing QR:', err);
        // Don't stop on network errors, just log them
      }
    }, 10000); // Every 10 seconds

    // Safety timeout - stop after 3.5 minutes (a bit longer than expected)
    timeoutRef.current = setTimeout(() => {
      console.log('QR session safety timeout triggered');
      stopQRGeneration();
    }, 210000); // 3.5 minutes
  };

  // Stop QR generation
  const stopQRGeneration = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    setIsActive(false);
    setQrCode(null);
    setTimeRemaining(0);
    setCurrentSequence(0);
  };

  // Manual stop (temporarily remove auth for testing)
  const stopSession = async (sessionId) => {
    try {
      console.log('Stopping QR session:', sessionId);
      
      const response = await fetch(`${config.API_BASE_URL}/qr-sessions/${sessionId}/stop-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('Stop session response:', data);
      
      if (data.success) {
        console.log('Session stopped successfully');
      } else {
        console.error('Failed to stop session:', data.message);
      }
      
      stopQRGeneration();
    } catch (err) {
      console.error('Error stopping session:', err);
    }
  };

  const value = {
    currentSession,
    qrCode,
    isActive,
    timeRemaining,
    currentSequence,
    loading,
    error,
    createSession,
    startQRGeneration,
    stopQRGeneration,
    stopSession
  };

  return (
    <QRSessionContext.Provider value={value}>
      {children}
    </QRSessionContext.Provider>
  );
};